<template>
    <div>
        <ModuleTitleNav :next-module="nextModule" :previous-module="previousModule" class="title-one">
            {{ moduleTitle }}
        </ModuleTitleNav>
        <div class="section-line" />
        <fieldset :disabled="formDisabled">
            <b-form-row>
                <b-col>
                    <p class="info-text">
                        Based on findings in the needs assessment, describe the career and technical education
                        course offerings and activities that the eligible recipient will provide with funds under
                        this part, including not less than 1 program of study approved by the State.
                    </p>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134 (b)(2)]
                    </div>
                    <div id="requiredPOS" class="field-labels">
                        Required Program of Study
                    </div>
                    <b-form-textarea id="requiredPOS-textarea" v-model="requiredPOS" :rows="3" />
                </b-col>
            </b-form-row>
        </fieldset>
        <b-form-row class="d-flex flex-column align-items-center">
            <div class="font-weight-bold attached-file-prefix">
                <div v-if="attachedFile.localApplicationUploadId">
                    <p>
                        Currently Attached File:
                        <span class="text-monospace font-weight-normal">
                            {{ attachedFile.originalFileName }}
                        </span>
                    </p>
                    <p class="mt-n3">
                        Uploaded On:
                        <span class="text-monospace font-weight-normal">
                            {{ (new Date(attachedFile.uploadedOn || null)).toLocaleString() }}
                        </span>
                    </p>
                </div>
                <p v-else>
                    No file attached, please upload a file and save
                </p>
            </div>
            <b-button class="mb-2"
                      size="sm"
                      variant="outline-primary"
                      :disabled="formDisabled"
                      @click="showUploadModal"
            >
                <fa-icon :icon="['fas', 'upload']" />
                Upload File
            </b-button>

            <b-button size="sm"
                      variant="outline-secondary"
                      data-toggle="tooltip"
                      data-placement="right"
                      :disabled="!attachedFile.localApplicationUploadId"
                      :title="attachedFile.originalFileName"
                      @click="downloadFile(attachedFile)"
            >
                <fa-icon :icon="['fas', 'download']" />
                View Attached Upload
            </b-button>
        </b-form-row>
        <fieldset :disabled="formDisabled">
            <div class="dotted-rule" />
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134 (b)(2)(A)]
                    </div>
                    <div class="info-text">
                        Describe how the results of the comprehensive needs assessment informed the selection of
                        the specific career and technical education programs and activities selected to be funded.
                    </div>
                    <b-form-textarea v-model="informed" :rows="3" />
                </b-col>
            </b-form-row>
            <br>
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134 (b)(2)(B)]
                    </div>
                    <div class="info-text">
                        Describe any new programs of study that will be
                        developed and submitted to the state for approval.
                    </div>
                    <b-form-textarea v-model="newPOS" :rows="3" />
                </b-col>
            </b-form-row>
            <br>
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134 (b)(2)(C)]
                    </div>
                    <div class="info-text">
                        Describe how all students will learn about career and technical education course offerings
                        and whether each course is part of a career and technical education program of study.
                    </div>
                    <b-form-textarea v-model="studentAwareness" :rows="3" />
                </b-col>
            </b-form-row>
            <br>
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134 (b)(2)(C)]
                    </div>
                    <div class="info-text">
                        Describe how students who are members of special populations will learn
                        about career and technical education course offerings and whether each
                        course is part of a career and technical education program of study.
                    </div>
                    <b-form-textarea v-model="specialAwareness" :rows="3" />
                </b-col>
            </b-form-row>
        </fieldset>
        <br>
        <b-form-row>
            <b-col>
                <LocalApplicationModuleActions
                    :is-loading="isSaving"
                    :is-disabled="formDisabled"
                    background-color="#006438"
                    @saveForm="saveForm"
                />
            </b-col>
        </b-form-row>

        <!-- file upload modal -->
        <!-- TODO: Extract this to a global component -->
        <b-modal v-if="!formDisabled" ref="fileUploadModalRef" title="Upload File">
            <p clas="my-4">
                Click to select a file, then click the upload button
            </p>
            <div class="d-block text-center">
                <b-form-file id="upload_file"
                             ref="fileUploadRef"
                             v-model="uploadingFile"
                             choose-label="Attachment"
                />
            </div>
            <hr>
            <p><strong>File Selected: </strong>{{ uploadingFile && uploadingFile.name }}</p>
            <div slot="modal-footer" class="w-100">
                <b-btn size="sm" variant="secondary" @click="hideUploadModal">
                    Cancel
                </b-btn>
                <b-btn size="sm" variant="success" @click="uploadFile">
                    Upload
                </b-btn>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';

const storeModuleName = 'cteOfferings';
// const storeModuleNamespace = 'localApplications/module';

export default {
    name: 'CteOfferings',

    props: {
        // formDisabled: { type: Boolean, default: true },
    },

    data() {
        return {
            isSaving: false,
            uploadingFile: null,
            moduleTitle: 'CTE Offerings',
            nextModule: { name: 'localApplication.WibCollaboration' },
            previousModule: { name: 'localApplication.ComprehensiveNeedsAssessmentSummary' },
        };
    },

    computed: {
        ...mapState('localApplication', {
            currentState: (state) => state.localApplicationData.state.currentState
        }),

        ...mapState(`localApplication/module/${storeModuleName}`, {
            uploadedFiles: (state) => state.data.content.uploadedFiles
        }),

        ...mapGetters({
            isAuthorized: 'auth/isAuthorized',
            localApplicationModuleContent: `localApplication/module/${storeModuleName}/content`
        }),

        formDisabled() {
            return !this.isAuthorized('update', 'local_application', this.currentState);
        },

        offeringActivities: {
            get() { return this.localApplicationModuleContent.offeringActivities; },
            set(value) { this.updateContentField({ field: 'offeringActivities', value }); }
        },

        requiredPOS: {
            get() { return this.localApplicationModuleContent.requiredPOS; },
            set(value) { this.updateContentField({ field: 'requiredPOS', value }); }
        },

        attachedFile: {
            get() { return this.localApplicationModuleContent.attachedFile; },
            set(v) { this.setAttachedFile(v); }
        },

        informed: {
            get() { return this.localApplicationModuleContent.informed; },
            set(value) { this.updateContentField({ field: 'informed', value }); }
        },

        newPOS: {
            get() { return this.localApplicationModuleContent.newPOS; },
            set(value) { this.updateContentField({ field: 'newPOS', value }); }
        },

        studentAwareness: {
            get() { return this.localApplicationModuleContent.studentAwareness; },
            set(value) { this.updateContentField({ field: 'studentAwareness', value }); }
        },

        specialAwareness: {
            get() { return this.localApplicationModuleContent.specialAwareness; },
            set(value) { this.updateContentField({ field: 'specialAwareness', value }); }
        }
    },

    updated() {
        if (this.parentId) {
            this.fetchUploads();
        }
    },

    methods: {
        ...mapActions(`localApplication/module/${storeModuleName}`, {
            saveLocalApplicationModule: 'save',
            updateContentField: 'updateContentField',
            setAttachedFile: 'setAttachedFile',
            uploadLocalApplicationFile: 'uploadFile',
            downloadFile: 'downloadFile',
            fetchUploads: 'fetchUploads'
        }),

        showUploadModal() {
            this.$refs.fileUploadModalRef.show();
        },

        hideUploadModal() {
            this.$refs.fileUploadRef.reset();
            this.uploadingFile = null;
            this.$refs.fileUploadModalRef.hide();
        },

        async uploadFile() {
            try {
                const data = await this.uploadLocalApplicationFile(this.uploadingFile);
                // NOTE: If this is the first upload, we'll go ahead and save the module
                // if (this.uploadedFiles.length === 0 && this.attachedFile === null) {
                //     this.attachedFile = data;
                //     this.saveForm();
                // }

                // TODO: Temporarily set the attached file, until we implement showing previously uploaded files
                //   and handling upload/selection through a generic component
                this.attachedFile = data;
                this.saveForm();
            } catch (error) {
                console.log(error);
            } finally {
                this.hideUploadModal();
            }
        },

        async saveForm() {
            this.isSaving = true;
            try {
                const response = await this.saveLocalApplicationModule();
                this.isSaving = false;
                Log.debug(response, `COMPONENT:${this.name} -> Save module response:`);
            } catch (error) {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style scoped>
.attached-file-prefix p {
    font-weight: bold;
    font-size: 0.9rem;
}
</style>
